import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { boards } from '../boardsRemote';
import { Board } from '../types';
import { useSession } from '../lib/auth-client';
import { ListBoardsOptions } from '../boards';

const BOARDS_QUERY_KEY = ['boards'];
const BOARDS_LIST_KEY = ['boards-list'];

export function useBoardsList(options: ListBoardsOptions = {}) {
  const { data: session } = useSession();
  const pageSize = 10;

  return useInfiniteQuery({
    queryKey: [...BOARDS_LIST_KEY, options],
    queryFn: async ({ pageParam }) => {
      return boards.list({
        search: options.search,
        filter: options.filter,
        visibility: options.visibility,
        sortBy: options.sortBy,
        sortOrder: options.sortOrder,
        limit: pageSize,
        cursor: pageParam as string | undefined,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!session?.user,
  });
}

/** @deprecated Use useBoardsList for paginated results */
export function useBoards() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: BOARDS_QUERY_KEY,
    queryFn: () => boards.getAll(),
    enabled: !!session?.user,
    refetchOnMount: 'always'
  });
}

export function useBoard(id: string | undefined) {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['board', id],
    queryFn: () => id ? boards.getById(id) : null,
    enabled: !!id && !!session?.user
  });
}

export function useCreateBoard(options?: {
  onSuccess?: (data: Board) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { title?: string, id?: string, expiresInHours?: number }) => boards.create(params.title, params.id, params.expiresInHours),
    onSuccess: (newBoard) => {
      queryClient.setQueryData(['board', newBoard.id], newBoard);
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
      options?.onSuccess?.(newBoard);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    }
  });
}

export function useUpdateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (board: Board) => boards.update(board),
    onMutate: async (board) => {
      await queryClient.cancelQueries({ queryKey: ['board', board.id] });
      queryClient.setQueryData(['board', board.id], board);

      return { board };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}

export function useDeleteBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => boards.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: BOARDS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: BOARDS_LIST_KEY });

      const previousBoards = queryClient.getQueryData<Board[]>(BOARDS_QUERY_KEY);
      if (previousBoards) {
        queryClient.setQueryData(BOARDS_QUERY_KEY, previousBoards.filter(b => b.id !== id));
      }

      return { previousBoards };
    },
    onError: (_err, _id, context) => {
      if (context?.previousBoards) {
        queryClient.setQueryData(BOARDS_QUERY_KEY, context.previousBoards);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}


export function useDuplicateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { board: Board; title?: string }) => {
      const { board, title } = params;
      const newTitle = title || `${board.title} (Copy)`;

      const newBoard = await boards.create(newTitle);

      await boards.update({
        ...newBoard,
        content: board.content,
        excalidrawElements: board.excalidrawElements,
      });

      const updatedBoard = await boards.getById(newBoard.id);
      return updatedBoard;
    },
    onSuccess: (newBoard) => {
      if (newBoard) {
        queryClient.setQueryData(['board', newBoard.id], newBoard);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}

export function useHistory(id: string | undefined) {
  const { data: session } = useSession();

  return useInfiniteQuery({
    queryKey: ['history', id],
    queryFn: async ({ pageParam }) => {
      if (!id) return { items: [], nextCursor: null };
      return boards.getHistory(id, 20, pageParam as number | undefined);
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!id && !!session?.user
  });
}

export function useRevertBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: string, version: number }) => boards.revert(params.id, params.version),
    onSuccess: (updatedBoard) => {
      queryClient.setQueryData(['board', updatedBoard.id], updatedBoard);
      queryClient.invalidateQueries({ queryKey: ['history', updatedBoard.id] });
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}

export function useToggleShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => boards.toggleShare(id),
    onSuccess: (updatedBoard) => {
      queryClient.setQueryData(['board', updatedBoard.id], updatedBoard);
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}

export function useTrackBoardAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => boards.trackAccess(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}

export function useRemoveSharedBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => boards.removeShared(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOARDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BOARDS_LIST_KEY });
    }
  });
}
